// Simple storage interface for in-memory use in Node.js
interface SimpleStorage {
  getItem: (key: string) => string | null
  setItem: (key: string, value: string) => void
  removeItem: (key: string) => void
}

// Module-level in-memory storage for environments where window is not available (Node.js)
const memoryStorage: Record<StorageType, Record<string, string>> = {
  localStorage: {},
  sessionStorage: {},
  temporaryStorage: {},
}

const getMemoryStorage = (type: StorageType): SimpleStorage => ({
  getItem: key => memoryStorage[type][key] ?? null,
  setItem: (key, value) => (memoryStorage[type][key] = value),
  removeItem: key => delete memoryStorage[type][key],
})

type WindowWithTemporaryStorage = typeof window & {
  temporaryStorage: {
    data: { [K in string]: string }
    setItem: (key: string, value: string) => void
    getItem: (key: string) => string | null
    removeItem: (key: string) => void
  }
}

type StorageType = 'sessionStorage' | 'localStorage' | 'temporaryStorage'

const getStorage = (type: StorageType): SimpleStorage | Storage => {
  if (typeof window !== 'undefined') {
    return (window as WindowWithTemporaryStorage)[type]
  }
  return getMemoryStorage(type)
}

// The fallback holds a value only while writing it to the real storage fails, and is read first
// meanwhile: a full quota rejects `setItem` but still serves the stale value through `getItem`.
// Keyed per storage, so a local and a session value under the same key stay apart.
const toFallbackKey = (storage: StorageType, storageKey: string) => `${storage}:${storageKey}`

/**
 * Returns whether the value reached the real storage rather than only this page's memory. A failed
 * write leaves the stored value in place: it is the last one that survives a reload, and dropping
 * it would lose data that has to outlive the page (follow-up's app lock PIN, say).
 */
export const storageSet = (storage: StorageType, storageKey: string, data: string) => {
  const fallbackKey = toFallbackKey(storage, storageKey)
  try {
    getStorage(storage).setItem(storageKey, data)
    getTemporaryStorage().removeItem(fallbackKey)
    return true
  } catch {
    getTemporaryStorage().setItem(fallbackKey, data)
    return false
  }
}

export const storageRemove = (storage: StorageType, storageKey: string) => {
  getTemporaryStorage().removeItem(toFallbackKey(storage, storageKey))
  try {
    getStorage(storage).removeItem(storageKey)
  } catch {
    // Blocked storage holds nothing to remove
  }
}

export const storageGet = (storage: StorageType, storageKey: string) => {
  const fallbackValue = getTemporaryStorage().getItem(toFallbackKey(storage, storageKey))
  if (fallbackValue !== null) {
    return fallbackValue
  }
  try {
    return getStorage(storage).getItem(storageKey)
  } catch {
    return null
  }
}

const getStorageTypes = (storageArea: Storage | null): StorageType[] => {
  try {
    if (storageArea === window.localStorage) {
      return ['localStorage']
    }
    if (storageArea === window.sessionStorage) {
      return ['sessionStorage']
    }
  } catch {
    // Telling the areas apart needs storage access, so a blocked one counts as either
  }
  return ['localStorage', 'sessionStorage']
}

const getTemporaryStorage = () => {
  if (typeof window === 'undefined') {
    return getMemoryStorage('temporaryStorage')
  }

  const w = window as WindowWithTemporaryStorage

  if (!w.temporaryStorage) {
    w.temporaryStorage = {
      data: {},
      setItem: (key, value) => (w.temporaryStorage.data[key] = value),
      getItem: key => w.temporaryStorage.data[key] ?? null,
      removeItem: key => delete w.temporaryStorage.data[key],
    }
    // Another tab changed the real storage, so what this page could only keep in memory for that key
    // is outdated: a logout there must not leave this page with its token and keys. Only the changed
    // key goes, because a login or logout writes the token and both keys together, while dropping
    // a whole area would let any unrelated write elsewhere cost this page its login. Registered with
    // the fallback, on the first read, which is before `useStorageItem` subscribes and re-reads.
    w.addEventListener('storage', ({ key, storageArea }) => {
      const changedStorages = getStorageTypes(storageArea)
      Object.keys(w.temporaryStorage.data)
        .filter(fallbackKey =>
          changedStorages.some(storage =>
            // A null key means the other tab cleared the whole storage
            key === null
              ? fallbackKey.startsWith(toFallbackKey(storage, ''))
              : fallbackKey === toFallbackKey(storage, key)
          )
        )
        .forEach(fallbackKey => w.temporaryStorage.removeItem(fallbackKey))
    })
  }
  return w.temporaryStorage
}
